/**
 * Every image field in the DB (User.avatar, CoreService.image, Promotions.image,
 * widget payload images, etc.) stores a bare Cloudinary public id — the web app
 * builds the display URL at the point of use (`https://res.cloudinary.com/${cloud}/image/upload/${id}`,
 * duplicated across dozens of client components). The mobile API instead
 * resolves these server-side before responding, since mobile has no reason to
 * know the Cloudinary cloud name or URL shape — see resolvers below.
 */
export function buildCloudinaryUrl(publicId?: string | null): string | null {
  if (!publicId) return null;
  // Already a full URL — message attachments are uploaded client-side and
  // stored as the Cloudinary secure_url already, not a bare public id.
  if (/^https?:\/\//.test(publicId)) return publicId;
  return `https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/upload/${publicId}`;
}

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

/**
 * Extensions Cloudinary's own `/auto/upload` classifies under resource_type
 * `image` (not just ones we render as an inline thumbnail) — PDF is the
 * notable one: Cloudinary treats PDFs as images (it can rasterize pages),
 * so a PDF's real asset genuinely lives under `/image/upload/`, not
 * `/raw/upload/`, even though we show it as a file/download bubble, not a
 * thumbnail, in the UI. Rewriting these to `/raw/upload/` 404s them.
 */
const CLOUDINARY_IMAGE_RESOURCE_EXTENSIONS = [...IMAGE_EXTENSIONS, "pdf"];

/**
 * Chat attachments are uploaded via Cloudinary's `/auto/upload` endpoint,
 * which picks `image` vs `raw` per file — but messages sent before that
 * resource-type detection was fixed can still have a `secure_url` stuck on
 * `/image/upload/` for a file Cloudinary would never classify as an image
 * (DOCX, XLSX, ...), which Cloudinary refuses to serve. Since the resource
 * type is baked into the URL path (not re-derivable from the file itself),
 * correct it here by extension whenever such a file's URL claims to be an
 * image.
 */
export function buildAttachmentUrl(
  fileUrl?: string | null,
  fileName?: string | null,
): string | null {
  const resolved = buildCloudinaryUrl(fileUrl);
  if (!resolved) return resolved;
  const ext = fileName?.split(".").pop()?.toLowerCase() || "";
  const isImage = CLOUDINARY_IMAGE_RESOURCE_EXTENSIONS.includes(ext);
  if (!isImage && resolved.includes("/image/upload/")) {
    return resolved.replace("/image/upload/", "/raw/upload/");
  }
  return resolved;
}

/** Resolves the avatar on a { avatar } shaped user/admin object, if present. */
export function withResolvedAvatar<T extends { avatar?: string | null }>(
  entity: T | null | undefined,
): T | null | undefined {
  if (!entity) return entity;
  return { ...entity, avatar: buildCloudinaryUrl(entity.avatar) as any };
}

/**
 * Resolves every image field a Feedback object can carry, depending on which
 * relations the caller's query happened to include — `user.avatar`,
 * `admin.avatar`, `coreService.image`, and any WIDGET message images in
 * `messages`. Each piece is a no-op if that relation wasn't selected.
 */
export function withResolvedFeedbackImages<
  T extends {
    user?: { avatar?: string | null } | null;
    admin?: { avatar?: string | null } | null;
    coreService?: { image?: string | null } | null;
    messages?: any[];
  },
>(feedback: T): T {
  return {
    ...feedback,
    user: feedback.user
      ? { ...feedback.user, avatar: buildCloudinaryUrl(feedback.user.avatar) }
      : feedback.user,
    admin: feedback.admin
      ? { ...feedback.admin, avatar: buildCloudinaryUrl(feedback.admin.avatar) }
      : feedback.admin,
    coreService: feedback.coreService
      ? { ...feedback.coreService, image: buildCloudinaryUrl(feedback.coreService.image) }
      : feedback.coreService,
    messages: feedback.messages?.map((m) => withResolvedWidgetImages(m)),
  };
}

/**
 * A WIDGET message's `widgetPayload` carries its own image field(s) — a
 * services list (each with an optional `image`) or a promotion (single
 * `image`) — see ServicesWidgetPayload / PromotionWidgetPayload in
 * app/_components/chat/widget-message.tsx. Booking widgets carry no image.
 */
export function withResolvedWidgetImages<
  T extends { type?: string; widgetKind?: string | null; widgetPayload?: unknown },
>(message: T): T {
  if (message.type !== "WIDGET" || !message.widgetPayload) return message;
  const payload = message.widgetPayload as any;

  if (message.widgetKind === "SERVICES" && Array.isArray(payload.services)) {
    return {
      ...message,
      widgetPayload: {
        ...payload,
        services: payload.services.map((s: any) => ({
          ...s,
          image: buildCloudinaryUrl(s.image),
        })),
      },
    };
  }

  if (message.widgetKind === "PROMOTION" && payload.image) {
    return {
      ...message,
      widgetPayload: { ...payload, image: buildCloudinaryUrl(payload.image) },
    };
  }

  return message;
}
